import { useMemo, useState } from 'react';

import { RICHTEXT_SETTINGS, ViewProps } from './table.constants';
import { getExtensions } from 'approot/shared/richtext/richtext.utils';
import { useActivityBuilderTheme } from 'approot/activity-builder/theme/activity-builder-theme';
import SubActivityPrint from '../sub-activity/sub-activity-print';
import { ColGroupsView } from './table-common';
import { getRichtextValue } from '../shared/logic/get-richtext-value';
import { getRichtextAnswer } from '../shared/logic/get-richtext-answer';
import {
  getTableAnswerKey,
  getTableAnswerMap,
} from '../shared/logic/table-answer-map';
import {
  getEditableCellsRowHeight,
  getTableHeaderAndBodyRows,
} from './table-view.utils';
import * as Styled from './table-print.style';

const TablePrint = ({
  properties,
  subActivityProperties,
  suggestedAnswerProperties,
  showAnswers,
}: ViewProps & {
  showAnswers?: boolean;
}) => {
  const table = properties.table;
  const extensions = getExtensions(RICHTEXT_SETTINGS);
  const [tableWrapperRef, setTableWrapperRef] = useState<HTMLDivElement | null>(
    null
  );
  const { theme } = useActivityBuilderTheme();

  const answersMap = useMemo(
    () =>
      showAnswers
        ? getTableAnswerMap(suggestedAnswerProperties?.suggestedAnswers || [])
        : null,
    [showAnswers, suggestedAnswerProperties?.suggestedAnswers]
  );

  const { tableHeaderRow, tableBodyRows } = useMemo(
    () => getTableHeaderAndBodyRows(table),
    [table]
  );

  const headerTextAreaRowHeight = useMemo(() => {
    return getEditableCellsRowHeight(tableHeaderRow?.cells || []);
  }, [tableHeaderRow]);

  return (
    <SubActivityPrint subActivityDetails={subActivityProperties}>
      <div ref={setTableWrapperRef}>
        <Styled.Table key={`rows-${table.rows.length}`}>
          <ColGroupsView
            columnWidths={table.columnWidths}
            containerElement={tableWrapperRef}
          />
          {tableHeaderRow && (
            <thead>
              <Styled.TableRow>
                {tableHeaderRow?.cells.map((header, index) => {
                  const answer = getRichtextAnswer(
                    answersMap?.[getTableAnswerKey(0, index)]
                  );
                  return (
                    <Styled.TableCell $highlight key={`row-1-column-${index}`}>
                      <Styled.TableCellView
                        $highlight
                        $baseFontSize={theme.font.medium.size}
                        $baseLineHeight={theme.font.small.lineHeight}
                        $isAnswer={!!answer}
                        $rows={headerTextAreaRowHeight}
                        dangerouslySetInnerHTML={{
                          __html: getRichtextValue(
                            answer || header.value,
                            extensions
                          ),
                        }}
                      />
                    </Styled.TableCell>
                  );
                })}
              </Styled.TableRow>
            </thead>
          )}
          <tbody>
            {tableBodyRows.map((row, tableBodyIndex) => {
              const rowIndex = table.showRowHeaders
                ? tableBodyIndex + 1
                : tableBodyIndex;
              const editorRows = getEditableCellsRowHeight(row.cells);

              return (
                <Styled.TableRow key={`row-${rowIndex}`}>
                  {row.cells.map((cell, cellIndex) => {
                    const answer = getRichtextAnswer(
                      answersMap?.[getTableAnswerKey(rowIndex, cellIndex)]
                    );
                    return (
                      <Styled.TableCell
                        key={`row-${rowIndex}-cell-${cellIndex}-header`}
                        $highlight={table.showColumnHeaders && cellIndex === 0}
                      >
                        <Styled.TableCellView
                          $highlight={
                            table.showColumnHeaders && cellIndex === 0
                          }
                          $baseFontSize={theme.font.medium.size}
                          $baseLineHeight={theme.font.small.lineHeight}
                          $isAnswer={!!answer}
                          $rows={editorRows}
                          dangerouslySetInnerHTML={{
                            __html: getRichtextValue(
                              answer || cell.value,
                              extensions
                            ),
                          }}
                        />
                      </Styled.TableCell>
                    );
                  })}
                </Styled.TableRow>
              );
            })}
          </tbody>
        </Styled.Table>
      </div>
    </SubActivityPrint>
  );
};

export default TablePrint;
